import { Stack, Button, Tooltip, ClickAwayListener, Box, Divider } from '@mui/material';
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  startTransition,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { flushSync } from 'react-dom';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parse, addMinutes } from 'date-fns';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import type { FlowSlotDto, FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@/hooks/use-notifications';
import { dateToMinutesFromMidnight } from '@/helpers/date.helper';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ArrowDropDownOutlined from '@mui/icons-material/ArrowDropDownOutlined';
import ArrowDropUpOutlined from '@mui/icons-material/ArrowDropUpOutlined';
import Expand from '@mui/icons-material/Expand';
import Compress from '@mui/icons-material/Compress';
import LockClockOutlined from '@mui/icons-material/LockClockOutlined';
import TimePicker from '@/components/common/time-picker';
import ConfirmationModal from '@/components/common/confirmation-modal';
import { useFlowSyncTransport } from '@/state/mutations/flows/use-flow-sync-transport';
import computeAllSlotTimings, { calculateFlowDurationFromSlots } from '../calculate';
import { computeSlotExpandShortenMetrics, isUntimedSlot } from '../slot-timing';
import { isMuiPortalDismissExemptTarget } from '../is-mui-portal-dismiss-exempt';
import useUpdateSessionMutation from '@/state/mutations/sessions/use-update-session';
import type { UpdateSessionDto } from '@/types/dtos/sessions/request/update-session.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowMode } from '../../use-flow-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { LuListStart, LuListEnd } from 'react-icons/lu';
import { PiSplitVertical } from 'react-icons/pi';
import { arrayMove } from '@dnd-kit/sortable';
import { useTheme } from '@mui/material/styles';
import { slotInlineContainedActionSx, slotInlineOutlinedActionSx } from '../slot-inline-action-buttons';
import { getSlotListColumnLayout } from '../slot-list-layout';
import { SlotListExpandedLeftColumn, slotListExpandedColumnPaddingSx } from '../slot-list-expanded-row-columns';
import {
  canSplitSessionSlot,
  computeNewSegmentDuration,
  getSessionContentId,
  getSessionSegmentSlots,
  shouldOfferSlotRemoval,
} from '../session-segment.helpers';

const slotTimingSchema = z.object({
  durationMinutes: z.string(),
  offsetMinutes: z.string().optional(),
});

type SlotTimingFormType = z.infer<typeof slotTimingSchema>;

type InlineSlotTimingFormProps = {
  slot: FlowSlotDto;
  gapAfter: number;
  onSlotUpdate?: (updatedSlot: FlowSlotDto) => void;
  onPreviewSlotUpdate?: (updatedSlot: FlowSlotDto) => void;
  cancelChanges?: () => void;
  onClose: () => void;
  onSlotDeleted?: () => void;
  localSlots?: FlowSlotDto[];
  previewFlow?: FlowDto;
  flowEndOffset?: number;
  /** Insert slot above/below; same as inline edit form */
  onShowForm?: (
    gapId: string,
    offset: number,
    slotType?: 'Session' | 'Break',
    insertBeforeSlotId?: string,
    insertAfterSlotId?: string,
  ) => void;
  /** Session Pool / untimed row: duration persists via session `plannedDurationMinutes`; middle actions always Cancel+Confirm. */
  isUntimed?: boolean;
  /** Match main slot row column widths (schedule drag-handle layout). */
  slotListColumnLayout?: import('../slot-list-layout').SlotListColumnLayout;
  /** Schedule tab while live: slot is in the playing-prefix of timed slots (no structural edits). */
  scheduleLiveLockBlocked?: boolean;
};

export type InlineSlotTimingFormHandle = {
  openDeleteDialog: () => void;
  /** Opens the fixed start-time picker (no-op if fixed time is off). */
  openFixedStartTimePicker: () => void;
};

const InlineSlotTimingForm = forwardRef<InlineSlotTimingFormHandle, InlineSlotTimingFormProps>(
  function InlineSlotTimingForm(
    {
      slot,
      onSlotUpdate,
      onPreviewSlotUpdate,
      cancelChanges,
      onClose,
      onSlotDeleted,
      localSlots,
      previewFlow,
      flowEndOffset: flowEndOffsetProp,
      onShowForm,
      isUntimed = false,
      slotListColumnLayout,
      scheduleLiveLockBlocked = false,
    },
    ref,
  ) {
    const columnLayout = slotListColumnLayout ?? getSlotListColumnLayout(false);
    const { t } = useTranslation();
    const notification = useNotifications();
    const flow = useFlowStore((s) => s.flow);
    const { emitFlowSync, canEmit } = useFlowSyncTransport();
    const sessions = useEventStore((s) => s.eventSessions);
    const { mutateAsync: updateSessionMutation } = useUpdateSessionMutation();
    const mode = useFlowMode();
    const theme = useTheme();
    const setSelectedSlot = useFlowStore((s) => s.setSelectedSlot);

    // Use preview flow if available for live preview, otherwise use actual flow
    const flowToUse = previewFlow ?? flow;
    const flowStartOffset = flowToUse?.slot?.startOffsetMinutes ?? 0;

    const slotEndOffset = useMemo(() => {
      const slotStart = slot?.computedOffsetMinutes ?? 0;
      const slotDuration = slot?.computedDurationMinutes ?? 0;
      return slotStart + slotDuration;
    }, [slot]);

    const previousSlotEndOffset = useMemo(() => {
      const slotsToUse = localSlots && localSlots.length > 0 ? localSlots : flowToUse?.slots;
      if (!slotsToUse || !slot) return 0;
      const slotIndex = slotsToUse.findIndex((s: FlowSlotDto) => s.id === slot.id);
      if (slotIndex === -1 || slotIndex === 0) return 0;
      const previousSlot = slotsToUse[slotIndex - 1];
      return (previousSlot.computedOffsetMinutes ?? 0) + (previousSlot.computedDurationMinutes ?? 0);
    }, [localSlots, flowToUse, slot]);
    // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
    const flowDuration = flowToUse?.durationPlannedMinutes;
    // Use provided flowEndOffset (from preview) if available, otherwise calculate it
    const flowEndOffset =
      flowEndOffsetProp ??
      (flowDuration !== undefined && flowDuration > 0 ? flowStartOffset + flowDuration : 24 * 60 - 1);
    const [localSlot, setLocalSlot] = useState(slot);
    const localSlotRef = useRef(slot);
    const onPreviewSlotUpdateRef = useRef(onPreviewSlotUpdate);
    const [showFixedTime, setShowFixedTime] = useState(slot?.plannedOffsetMinutes !== undefined);
    const [initialShowFixedTime, setInitialShowFixedTime] = useState(slot?.plannedOffsetMinutes !== undefined);
    const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
    /** Visible anchor for the fixed start-time Popper (hidden TimePicker field has no layout box). */
    const [fixedStartPickerAnchorEl, setFixedStartPickerAnchorEl] = useState<HTMLElement | null>(null);
    const [pendingTimeValue, setPendingTimeValue] = useState<string | null>(null);
    const isSelectingTimeRef = useRef(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const isDeleteDialogOpenRef = useRef(false);
    const [deletePending, setDeletePending] = useState(false);
    const [splitPending, setSplitPending] = useState(false);
    const [isDeletingSessionNotInLibrary, setIsDeletingSessionNotInLibrary] = useState(false);

    useEffect(() => {
      isDeleteDialogOpenRef.current = isDeleteDialogOpen;
    }, [isDeleteDialogOpen]);

    const openDeleteDialog = useCallback(() => {
      if (scheduleLiveLockBlocked) return;
      if (slot?.contentType === 'Session' && slot?.content) {
        setIsDeletingSessionNotInLibrary(!shouldOfferSlotRemoval(slot, flow));
      } else {
        setIsDeletingSessionNotInLibrary(false);
      }
      setIsDeleteDialogOpen(true);
    }, [scheduleLiveLockBlocked, slot, flow]);

    const timingMutationDisabled = isOrganizeMode(mode) || scheduleLiveLockBlocked;

    // Keep ref updated
    useEffect(() => {
      onPreviewSlotUpdateRef.current = onPreviewSlotUpdate;
    }, [onPreviewSlotUpdate]);

    // Detect if device has touch capability (mobile/tablet) - for timing delays
    const isTouchDevice = useMemo(() => {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }, []);

    // Track current duration for the form
    const [currentDuration, setCurrentDuration] = useState(
      localSlot?.plannedDurationMinutes ?? localSlot?.computedDurationMinutes ?? 0,
    );

    // Build timed slot list for expand/shorten metrics (reflects preview + in-form edits).
    const expandShortenMetrics = useMemo(() => {
      const baseSlots = (localSlots && localSlots.length > 0 ? localSlots : flowToUse?.slots) ?? [];
      const timedSlots = baseSlots.filter((s) => s.contentType !== 'Gap' && !isUntimedSlot(s));
      if (!timedSlots.length) {
        return { gap: 0, shortenedBy: 0, hasFixedBoundary: false };
      }

      const slotsWithLocalEdits = timedSlots.map((s) => {
        if (s.id !== slot.id) return s;
        return {
          ...s,
          plannedDurationMinutes: currentDuration,
          plannedOffsetMinutes: showFixedTime ? localSlot?.plannedOffsetMinutes : undefined,
        };
      });

      const fixedFlowDuration = flowDuration !== undefined && flowDuration > 0 ? flowDuration : undefined;

      return computeSlotExpandShortenMetrics(slotsWithLocalEdits, slot.id, {
        flowDuration: fixedFlowDuration,
        editingPlannedDuration: currentDuration,
      });
    }, [
      localSlots,
      flowToUse?.slots,
      slot.id,
      currentDuration,
      showFixedTime,
      localSlot?.plannedOffsetMinutes,
      flowDuration,
    ]);

    const { gap: gapBeforeFixedSlot, shortenedBy: slotShortenedBy, hasFixedBoundary } = expandShortenMetrics;

    // Track if duration change is coming from button click (to prevent circular updates)
    const isUpdatingFromButtonRef = useRef(false);
    // Track if we're resetting the form (to prevent preview updates during reset)
    const isResettingRef = useRef(false);
    // Track if change is from fill/shorten buttons (to mark form as dirty)
    const isFillShortenActionRef = useRef(false);

    const { handleSubmit, setValue, reset, formState, control, getValues } = useForm<SlotTimingFormType>({
      resolver: zodResolver(slotTimingSchema),
      defaultValues: {
        durationMinutes: minuteOffsetToTime(
          localSlot?.plannedDurationMinutes ?? localSlot?.computedDurationMinutes ?? 0,
        ),
        offsetMinutes: minuteOffsetToTime(
          showFixedTime && localSlot?.plannedOffsetMinutes !== undefined
            ? flowStartOffset + localSlot.plannedOffsetMinutes
            : flowStartOffset + (localSlot?.computedOffsetMinutes ?? 0),
        ),
      },
    });

    const openFixedStartTimePicker = useCallback(() => {
      if (scheduleLiveLockBlocked || !showFixedTime) return;
      const current = getValues('offsetMinutes');
      isSelectingTimeRef.current = false;
      setPendingTimeValue(current || null);
      setIsTimePickerOpen(true);
    }, [scheduleLiveLockBlocked, showFixedTime, getValues]);

    useImperativeHandle(ref, () => ({ openDeleteDialog, openFixedStartTimePicker }), [
      openDeleteDialog,
      openFixedStartTimePicker,
    ]);

    // Reset form when slot changes
    useEffect(() => {
      localSlotRef.current = slot;
      isSelectingTimeRef.current = false; // Reset selection flag when slot changes
      const newShowFixedTime = slot?.plannedOffsetMinutes !== undefined;
      queueMicrotask(() => {
        setLocalSlot(slot);
        setShowFixedTime(newShowFixedTime);
        setInitialShowFixedTime(newShowFixedTime);
        setIsTimePickerOpen(false); // Close time picker when slot changes
        setPendingTimeValue(null); // Reset pending value when slot changes
        setCurrentDuration(slot?.plannedDurationMinutes ?? slot?.computedDurationMinutes ?? 0);
      });
      reset({
        durationMinutes: minuteOffsetToTime(slot?.plannedDurationMinutes ?? slot?.computedDurationMinutes ?? 0),
        offsetMinutes: minuteOffsetToTime(
          slot?.plannedOffsetMinutes !== undefined
            ? flowStartOffset + slot.plannedOffsetMinutes
            : flowStartOffset + (slot?.computedOffsetMinutes ?? 0),
        ),
      });
    }, [slot.id, flowStartOffset, reset]);

    // When fixed time is disabled (!showFixedTime), sync computedOffsetMinutes from slot prop
    // This ensures the computed time display updates correctly after recalculation
    useEffect(() => {
      if (
        !showFixedTime &&
        slot?.computedOffsetMinutes !== undefined &&
        localSlot?.plannedOffsetMinutes === undefined
      ) {
        queueMicrotask(() => {
          setLocalSlot((prevSlot) => {
            if (prevSlot?.computedOffsetMinutes !== slot.computedOffsetMinutes) {
              return {
                ...prevSlot,
                computedOffsetMinutes: slot.computedOffsetMinutes,
              };
            }
            return prevSlot;
          });
        });
      }
    }, [showFixedTime, slot?.computedOffsetMinutes, localSlot?.plannedOffsetMinutes]);

    // Sync form values when timing changes
    const lastDurationRef = useRef<number | undefined>(currentDuration);
    const isSyncingFromPropRef = useRef(false);

    useEffect(() => {
      if (currentDuration !== undefined && lastDurationRef.current !== currentDuration) {
        // If change is from fill/shorten buttons, mark form as dirty
        if (isFillShortenActionRef.current) {
          isFillShortenActionRef.current = false;
          setValue('durationMinutes', minuteOffsetToTime(currentDuration), {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          });
        } else {
          isSyncingFromPropRef.current = true;
          startTransition(() => {
            setValue('durationMinutes', minuteOffsetToTime(currentDuration), {
              shouldDirty: false,
              shouldValidate: false,
              shouldTouch: false,
            });
          });
        }
        lastDurationRef.current = currentDuration;
        requestAnimationFrame(() => {
          isSyncingFromPropRef.current = false;
        });
      }
    }, [currentDuration, setValue]);

    // Watch for form changes and update local state
    const durationMinutes = useWatch({ control, name: 'durationMinutes' });
    const offsetMinutes = useWatch({ control, name: 'offsetMinutes' });

    // Update local state when form values change
    useEffect(() => {
      if (!onPreviewSlotUpdateRef.current || isResettingRef.current) return;

      const duration = durationMinutes ? dateToMinutesFromMidnight(durationMinutes) : 0;

      const offset =
        showFixedTime && offsetMinutes ? dateToMinutesFromMidnight(offsetMinutes) - flowStartOffset : undefined;

      setLocalSlot((prevSlot) => {
        const updatedSlot = {
          ...prevSlot,
          plannedDurationMinutes: duration > 0 ? duration : undefined,
          plannedOffsetMinutes: offset,
          // Keep computedDurationMinutes from prevSlot to avoid stale values
          // It will be updated when preview callback completes
          computedDurationMinutes: prevSlot?.computedDurationMinutes,
        };
        localSlotRef.current = updatedSlot;
        // Call preview update with the updated slot using ref to avoid dependency issues
        setTimeout(() => {
          onPreviewSlotUpdateRef.current?.(updatedSlot);
        }, 0);
        return updatedSlot;
      });
    }, [durationMinutes, offsetMinutes, showFixedTime, flowStartOffset]);

    // Watch durationMinutes and update currentDuration (but not when syncing from currentDuration prop or button updates)
    // This effect should only run when the user manually types in the form field, not when buttons update it
    useEffect(() => {
      if (durationMinutes !== undefined && !isSyncingFromPropRef.current && !isUpdatingFromButtonRef.current) {
        const timeoutId = setTimeout(() => {
          // Double-check flag is still false (button update might have happened during timeout)
          if (!isUpdatingFromButtonRef.current && !isSyncingFromPropRef.current) {
            const rawDuration = dateToMinutesFromMidnight(durationMinutes);
            const duration = Math.max(1, rawDuration); // Ensure minimum of 1 minute
            // Only update if it's different to avoid unnecessary re-renders
            setCurrentDuration((prev) => {
              if (prev !== duration) {
                return duration;
              }
              return prev;
            });
            // Update form field if duration was clamped
            if (duration !== rawDuration) {
              const clampedTime = format(addMinutes(parse('00:00', 'HH:mm', new Date()), duration), 'HH:mm');
              setValue('durationMinutes', clampedTime, {
                shouldDirty: false,
                shouldValidate: false,
                shouldTouch: false,
              });
            }
          }
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }, [durationMinutes, setValue]);

    // Convert duration to HH:mm format for OffsetButtons
    const durationAsTime = useMemo(
      () => format(addMinutes(parse('00:00', 'HH:mm', new Date()), currentDuration), 'HH:mm'),
      [currentDuration],
    );

    // Handle duration change from OffsetButtons
    const handleDurationChange = useCallback(
      (newDurationTime: string) => {
        const date = parse(newDurationTime, 'HH:mm', new Date());
        const minutes = Math.max(1, date.getHours() * 60 + date.getMinutes()); // Ensure minimum of 1 minute
        isUpdatingFromButtonRef.current = true;
        setCurrentDuration(minutes);
        // Also update the form field directly with clamped value
        const clampedTime = format(addMinutes(parse('00:00', 'HH:mm', new Date()), minutes), 'HH:mm');
        setValue('durationMinutes', clampedTime, {
          shouldDirty: true,
          shouldValidate: false,
          shouldTouch: false,
        });
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isUpdatingFromButtonRef.current = false;
            });
          });
        }, 50);
      },
      [setValue],
    );

    // Long-press support for OffsetButtons (reused from create form)
    const longPressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const longPressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isLongPressingRef = useRef(false);
    const pendingTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
    const touchStartTimeRef = useRef<number>(0);
    const activeTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const removeGlobalReleaseListenersRef = useRef<(() => void) | null>(null);
    const handleLongPressEnd = useCallback(() => {
      const currentTimeoutId = longPressTimeoutRef.current;

      if (removeGlobalReleaseListenersRef.current) {
        removeGlobalReleaseListenersRef.current();
        removeGlobalReleaseListenersRef.current = null;
      }

      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
        if (activeTimeoutIdRef.current === currentTimeoutId) {
          activeTimeoutIdRef.current = null;
        }
      }

      if (longPressIntervalRef.current) {
        clearInterval(longPressIntervalRef.current);
        longPressIntervalRef.current = null;
      }

      pendingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      pendingTimeoutsRef.current.clear();

      isLongPressingRef.current = false;
    }, []);

    const handleLongPressStart = useCallback(
      (minutes: number) => {
        if (scheduleLiveLockBlocked) return;
        if (longPressTimeoutRef.current) {
          clearTimeout(longPressTimeoutRef.current);
          longPressTimeoutRef.current = null;
          activeTimeoutIdRef.current = null;
        }
        if (longPressIntervalRef.current) {
          clearInterval(longPressIntervalRef.current);
          longPressIntervalRef.current = null;
        }

        if (removeGlobalReleaseListenersRef.current) {
          removeGlobalReleaseListenersRef.current();
          removeGlobalReleaseListenersRef.current = null;
        }

        pendingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
        pendingTimeoutsRef.current.clear();

        touchStartTimeRef.current = Date.now();
        isLongPressingRef.current = false;

        if (isTouchDevice) {
          const onRelease = () => handleLongPressEnd();
          const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') handleLongPressEnd();
          };

          window.addEventListener('touchend', onRelease, { capture: true, passive: true });
          window.addEventListener('touchcancel', onRelease, { capture: true, passive: true });
          window.addEventListener('pointerup', onRelease, { capture: true });
          window.addEventListener('pointercancel', onRelease, { capture: true });
          window.addEventListener('blur', onRelease, { capture: true });
          document.addEventListener('visibilitychange', onVisibilityChange, { capture: true });

          removeGlobalReleaseListenersRef.current = () => {
            const options: EventListenerOptions = { capture: true };
            window.removeEventListener('touchend', onRelease, options);
            window.removeEventListener('touchcancel', onRelease, options);
            window.removeEventListener('pointerup', onRelease, options);
            window.removeEventListener('pointercancel', onRelease, options);
            window.removeEventListener('blur', onRelease, options);
            document.removeEventListener('visibilitychange', onVisibilityChange, options);
          };
        } else {
          removeGlobalReleaseListenersRef.current = () => {
            // No-op on desktop
          };
        }

        const timeoutId = setTimeout(() => {
          if (activeTimeoutIdRef.current === timeoutId && longPressTimeoutRef.current === timeoutId) {
            isLongPressingRef.current = true;

            const intervalId = setInterval(() => {
              if (longPressIntervalRef.current !== intervalId) {
                clearInterval(intervalId);
                return;
              }

              setCurrentDuration((prev) => {
                const newDuration = Math.max(1, prev + minutes);
                const newDurationTime = format(addMinutes(parse('00:00', 'HH:mm', new Date()), newDuration), 'HH:mm');
                // Set flag before updating form field to prevent effect from interfering
                isUpdatingFromButtonRef.current = true;
                // Update form field
                setValue('durationMinutes', newDurationTime, {
                  shouldDirty: true,
                  shouldValidate: false,
                  shouldTouch: false,
                });
                const timeoutId = setTimeout(() => {
                  if (longPressIntervalRef.current === intervalId) {
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        isUpdatingFromButtonRef.current = false;
                      });
                    });
                  }
                  pendingTimeoutsRef.current.delete(timeoutId);
                }, 50);
                pendingTimeoutsRef.current.add(timeoutId);
                return newDuration;
              });
            }, 50);

            longPressIntervalRef.current = intervalId;
          }
        }, 500);

        longPressTimeoutRef.current = timeoutId;
        activeTimeoutIdRef.current = timeoutId;
      },
      [scheduleLiveLockBlocked, isTouchDevice, setValue, handleLongPressEnd],
    );

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (removeGlobalReleaseListenersRef.current) {
          removeGlobalReleaseListenersRef.current();
          removeGlobalReleaseListenersRef.current = null;
        }

        if (longPressTimeoutRef.current) {
          clearTimeout(longPressTimeoutRef.current);
        }
        if (longPressIntervalRef.current) {
          clearInterval(longPressIntervalRef.current);
        }
        pendingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
        pendingTimeoutsRef.current.clear();
      };
    }, []);

    // Enhanced OffsetButtons usage - component is now imported from common

    // Session info
    const sessionTitle = slot?.contentType === 'Session' ? slot?.content?.title : slot?.title;
    const showSplitSessionButton =
      slot?.contentType === 'Session' && !isUntimed && canSplitSessionSlot(flow, slot) && canEmit;

    const handleSplitSession = useCallback(async () => {
      if (!flow || !slot || slot.contentType !== 'Session' || !slot.content || isUntimed) return;
      if (!canSplitSessionSlot(flow, slot) || !canEmit) return;

      setSplitPending(true);

      const sessionContentId = getSessionContentId(slot);
      const segmentSlots = sessionContentId ? getSessionSegmentSlots(flow, sessionContentId) : [];
      const durationMinutes = computeNewSegmentDuration(slot.content, segmentSlots);

      const generateTempId = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
          return `temp-${crypto.randomUUID()}`;
        }
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(7);
        return `temp-${timestamp}-${random}`;
      };

      const tempSlotId = generateTempId();
      const newSlot: FlowSlotDto = {
        id: tempSlotId,
        contentType: 'Session',
        content: slot.content,
        plannedDurationMinutes: durationMinutes,
        computedDurationMinutes: durationMinutes,
        isUntimed: false,
        plannedOffsetMinutes: undefined,
        computedOffsetMinutes: undefined,
      };

      const newSlots = [...(flow.slots ?? []), newSlot];
      const insertionIndex = flow.slots?.findIndex((s) => s.id === slot.id) ?? -1;
      const insertAt = insertionIndex === -1 ? newSlots.length - 1 : insertionIndex + 1;
      const reorderedSlots = arrayMove(newSlots, newSlots.length - 1, insertAt);

      const durationToUse = flow.durationPlannedMinutes ?? calculateFlowDurationFromSlots(reorderedSlots);
      const computedSlots = computeAllSlotTimings(reorderedSlots, durationToUse);

      let startTime: string | undefined;
      let endTime: string | null | undefined;

      if (flow.slot?.startOffsetMinutes !== undefined) {
        startTime = minuteOffsetToTime(flow.slot.startOffsetMinutes);
        const flowDurationPlannedMinutes = flow.durationPlannedMinutes;
        if (flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0) {
          endTime = minuteOffsetToTime(flow.slot.startOffsetMinutes + flowDurationPlannedMinutes);
        } else {
          endTime = null;
        }
      }

      const normalizedSlots = computedSlots.map((flowSlot) => {
        const normalized = {
          ...flowSlot,
          content:
            flowSlot.contentType === 'Gap'
              ? undefined
              : typeof flowSlot.content === 'object'
                ? flowSlot.content?.id
                : flowSlot.content,
        };
        if (flowSlot.id === tempSlotId) {
          delete (normalized as { id?: string }).id;
        }
        return normalized;
      });

      const flowPayload = {
        ...flow,
        slots: normalizedSlots,
        slot: undefined,
        startDate: undefined,
        ...(startTime && { startTime }),
        ...(startTime !== undefined && { endTime: endTime ?? null }),
      };

      const setFlow = useFlowStore.getState().setFlow;
      const optimisticFlow: FlowDto = {
        ...flow,
        slots: computedSlots,
        version: (flow.version ?? 0) + 1,
        ...(startTime && { startTime }),
        ...(startTime !== undefined && { endTime: endTime ?? null }),
      };
      setFlow(optimisticFlow);

      const timeoutId = setTimeout(() => {
        setFlow(flow);
        notification.notifyError(t('flows.detail.slotList.detail.splitSession.notifications.error'));
        setSplitPending(false);
      }, 10000);

      void (async () => {
        const { ok } = await emitFlowSync({ flow: flowPayload as Record<string, unknown> });
        clearTimeout(timeoutId);

        if (ok) {
          notification.notifySuccess(
            t('flows.detail.slotList.detail.splitSession.notifications.success', { title: sessionTitle || '' }),
          );
          onClose();
        } else {
          setFlow(flow);
          notification.notifyError(t('flows.detail.slotList.detail.splitSession.notifications.error'));
        }
        setSplitPending(false);
      })();
    }, [flow, slot, isUntimed, canEmit, emitFlowSync, notification, t, sessionTitle, onClose]);

    // Expand/Reduce handlers
    const handleExpandToFillGap = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (!flow || gapBeforeFixedSlot <= 0 || currentDuration <= 0) return;

        const newDuration = currentDuration + gapBeforeFixedSlot;
        isFillShortenActionRef.current = true;
        setCurrentDuration(newDuration);
        setValue('durationMinutes', minuteOffsetToTime(newDuration), {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
      },
      [flow, gapBeforeFixedSlot, currentDuration, setValue],
    );

    const handleShorten = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (!flow || currentDuration <= 0) return;

        const overlapAmount =
          gapBeforeFixedSlot < 0 ? Math.abs(gapBeforeFixedSlot) : slotShortenedBy > 0 ? slotShortenedBy : 0;
        if (overlapAmount <= 0) return;

        const newDuration = Math.max(1, currentDuration - overlapAmount);
        isFillShortenActionRef.current = true;
        setCurrentDuration(newDuration);
        setValue('durationMinutes', minuteOffsetToTime(newDuration), {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
      },
      [flow, gapBeforeFixedSlot, slotShortenedBy, currentDuration, setValue],
    );

    // Fixed time toggle handler (mirror inline end-timing: open time picker when enabling fixed time)
    const handleToggleFixedTime = useCallback(() => {
      if (isUntimed || scheduleLiveLockBlocked) return;
      if (showFixedTime) {
        setIsTimePickerOpen(false);
        // Remove fixed time - update to show computed start-time from original slot
        setShowFixedTime(false);
        // Use the computed offset from the original slot (not localSlot) to show the correct computed time
        const computedOffset = slot?.computedOffsetMinutes ?? localSlot?.computedOffsetMinutes ?? 0;
        const updatedSlot = {
          ...localSlot,
          plannedOffsetMinutes: undefined,
          // Update computedOffsetMinutes to match the original slot's computed value
          computedOffsetMinutes: computedOffset,
        };
        setLocalSlot(updatedSlot);
        // Update form to show computed start-time (though it won't be displayed when showFixedTime is false)
        setValue('offsetMinutes', minuteOffsetToTime(flowStartOffset + computedOffset));
        onPreviewSlotUpdate?.(updatedSlot);
      } else {
        if (isTimePickerOpen) {
          setIsTimePickerOpen(false);
        }
        // Set fixed time to current computed time
        setShowFixedTime(true);
        const currentOffset = localSlot?.computedOffsetMinutes ?? 0;
        const offsetTimeStr = minuteOffsetToTime(flowStartOffset + currentOffset);
        const updatedSlot = {
          ...localSlot,
          plannedOffsetMinutes: currentOffset,
        };
        setLocalSlot(updatedSlot);
        setValue('offsetMinutes', offsetTimeStr);
        onPreviewSlotUpdate?.(updatedSlot);
        isSelectingTimeRef.current = false;
        setPendingTimeValue(offsetTimeStr);
        setIsTimePickerOpen(true);
      }
    }, [
      isUntimed,
      scheduleLiveLockBlocked,
      showFixedTime,
      localSlot,
      slot,
      flowStartOffset,
      setValue,
      onPreviewSlotUpdate,
      isTimePickerOpen,
    ]);

    // Delete handlers
    const handleDeleteModalCancel = useCallback(() => {
      setIsDeleteDialogOpen(false);
      setIsDeletingSessionNotInLibrary(false);
    }, []);

    const handleDeleteSession = useCallback(async () => {
      if (!flow) return;

      setDeletePending(true);
      const filteredSlots = flow.slots?.filter((s) => s.id !== slot.id);
      // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
      const durationToUse = flow.durationPlannedMinutes ?? calculateFlowDurationFromSlots(filteredSlots ?? []);
      const computedSlots = computeAllSlotTimings(filteredSlots ?? [], durationToUse);

      // Extract startTime and endTime from slot if present (matching syncFlow pattern)
      // This preserves the fixed end-time (durationPlannedMinutes) when deleting slots
      let startTime: string | undefined;
      let endTime: string | null | undefined;

      if (flow.slot?.startOffsetMinutes !== undefined) {
        startTime = minuteOffsetToTime(flow.slot.startOffsetMinutes);

        // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
        const flowDurationPlannedMinutes = flow.durationPlannedMinutes;
        // Check if durationPlannedMinutes is explicitly set and greater than 0
        if (flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0) {
          // Fixed end-time is set
          endTime = minuteOffsetToTime(flow.slot.startOffsetMinutes + flowDurationPlannedMinutes);
        } else {
          // Fixed end-time is being removed (undefined, null, or <= 0) - explicitly set to null to clear it
          endTime = null;
        }
      }

      // Payload to send to server - use original version
      // Follow the same pattern as syncFlow: convert slot to startTime/endTime strings
      const flowPayload = {
        ...flow,
        slots: computedSlots,
        slot: undefined, // Remove slot object - backend expects startTime/endTime strings
        startDate: undefined,
        ...(startTime && { startTime }),
        // Always include endTime when startTime is present, even if null (to clear it)
        ...(startTime !== undefined && { endTime: endTime ?? null }),
        // Keep original version - don't increment here
      };

      // Update session if it's the last usage
      const session = sessions?.find((s) => s.id === slot?.content?.id);
      if (session) {
        const isLastUsageOfContent = flow?.slots?.filter((s) => s.content?.id === session.id).length === 1;
        if (isLastUsageOfContent) {
          const updateSessionDto: UpdateSessionDto = {
            id: session.id,
            version: session.version,
            usedInEntities: session.usedInEntities
              ?.filter((e) => e.entity.id !== flow?.id)
              .map((e) => ({
                entity: e.entity.id,
                entityType: e.entityType,
              })),
          };

          try {
            await updateSessionMutation(updateSessionDto);
          } catch {
            notification.notifyError(t('flows.detail.slotList.detail.deleteSlot.notifications.deleteError'));
            setDeletePending(false);
            return;
          }
        }
      }

      if (!canEmit) {
        notification.notifyError(t('flows.detail.slotList.detail.deleteSlot.notifications.deleteError'));
        setDeletePending(false);
        return;
      }

      const setFlow = useFlowStore.getState().setFlow;
      const optimisticFlow = {
        ...flowPayload,
        version: (flow.version ?? 0) + 1,
      };
      setFlow(optimisticFlow);

      const timeoutId = setTimeout(() => {
        setFlow(flow);
        notification.notifyError(t('flows.detail.slotList.detail.deleteSlot.notifications.deleteError'));
        setDeletePending(false);
        setIsDeleteDialogOpen(false);
      }, 10000);

      void (async () => {
        const { ok } = await emitFlowSync({ flow: flowPayload as Record<string, unknown> });
        clearTimeout(timeoutId);

        if (ok) {
          notification.notifySuccess(
            isDeletingSessionNotInLibrary
              ? t('flows.detail.slotList.detail.deleteSlot.notifications.deleteSuccess', { title: sessionTitle || '' })
              : t('flows.detail.slotList.detail.deleteSlot.notifications.removeSessionSuccess', {
                  title: sessionTitle || '',
                }),
          );
          setIsDeleteDialogOpen(false);
          setSelectedSlot(undefined);
          onClose();
          onSlotDeleted?.();
        } else {
          setFlow(flow);
          notification.notifyError(t('flows.detail.slotList.detail.deleteSlot.notifications.deleteError'));
          setDeletePending(false);
          setIsDeleteDialogOpen(false);
        }
      })();
    }, [
      flow,
      slot,
      sessions,
      updateSessionMutation,
      emitFlowSync,
      canEmit,
      isDeletingSessionNotInLibrary,
      sessionTitle,
      setSelectedSlot,
      onClose,
      onSlotDeleted,
      notification,
      t,
    ]);

    const handleCancel = useCallback(() => {
      // Set resetting flag to prevent preview updates during reset
      isResettingRef.current = true;

      // Reset form values FIRST before cancelChanges (which unmounts the component)
      reset({
        durationMinutes: minuteOffsetToTime(slot?.plannedDurationMinutes ?? slot?.computedDurationMinutes ?? 0),
        offsetMinutes: minuteOffsetToTime(
          slot?.plannedOffsetMinutes !== undefined
            ? flowStartOffset + slot.plannedOffsetMinutes
            : flowStartOffset + (slot?.computedOffsetMinutes ?? 0),
        ),
      });
      setLocalSlot(slot);
      setShowFixedTime(slot?.plannedOffsetMinutes !== undefined);
      setCurrentDuration(slot?.plannedDurationMinutes ?? slot?.computedDurationMinutes ?? 0);

      // Now cancel preview changes and close (this will unmount the component)
      cancelChanges?.();
      onClose();
    }, [cancelChanges, onClose, reset, slot, flowStartOffset]);

    const handleClickAway = useCallback(
      (event: MouseEvent | TouchEvent) => {
        // On mobile, touch events need more time to propagate and execute
        // The ClickAwayListener fires on mouseDown/touchStart, but we need to wait
        // for the click/tap to complete before closing
        const target = event.target as HTMLElement;
        const isTouchEvent = 'touches' in event || 'changedTouches' in event;

        const inlineDetailsRoot = target.closest('[data-slot-inline-details]');
        if (inlineDetailsRoot instanceof HTMLElement && inlineDetailsRoot.getAttribute('data-slot-id') === slot.id) {
          return;
        }

        if (isMuiPortalDismissExemptTarget(event)) {
          return;
        }

        // Delete control is rendered in item.tsx outside this ClickAwayListener; taps would otherwise
        // count as "away" and schedule onClose (mobile: ~100ms), unmounting before the modal works.
        if (target.closest('[data-slot-timing-delete]')) {
          return;
        }

        if (isDeleteDialogOpenRef.current) {
          return;
        }

        // Check if clicking on another slot (to open its form).
        // The timing section sometimes isn't inside the same element that holds `data-slot-id`,
        // so we search the composedPath + ancestors for common slot id attributes.
        const getClickedSlotId = () => {
          const path = (event as Event & { composedPath?: () => EventTarget[] })?.composedPath?.();
          const candidates: (HTMLElement | EventTarget)[] = [];

          if (path && Array.isArray(path)) candidates.push(...path);
          candidates.push(target);

          // Walk up the DOM as a fallback
          let el: HTMLElement | null = target;
          while (el) {
            candidates.push(el);
            el = el.parentElement;
          }

          // Filter to only HTMLElement nodes that have getAttribute method
          const htmlElements = candidates.filter((node): node is HTMLElement => node instanceof HTMLElement);

          for (const node of htmlElements) {
            // Support a few possible attribute names used across slot cards
            const id =
              node.getAttribute('data-slot-id') ||
              node.getAttribute('data-flow-slot-id') ||
              node.getAttribute('data-slotid') ||
              node.getAttribute('data-flow-slotid');

            if (id) return id;
          }

          // As a last resort, try finding the nearest element that contains any of these attributes
          const closest = target.closest(
            '[data-slot-id],[data-flow-slot-id],[data-slotid],[data-flow-slotid]',
          ) as HTMLElement | null;
          return (
            closest?.getAttribute('data-slot-id') ||
            closest?.getAttribute('data-flow-slot-id') ||
            closest?.getAttribute('data-slotid') ||
            closest?.getAttribute('data-flow-slotid') ||
            null
          );
        };

        const clickedSlotId = getClickedSlotId();
        const isClickingAnotherSlot = !!clickedSlotId && clickedSlotId !== slot.id;

        // Check if clicking on timing section or middle section of ANY slot
        // These sections handle their own clicks and should not trigger close
        const clickedSlotCard = target.closest('[data-testid="flow-slot"]') as HTMLElement | null;
        if (clickedSlotCard) {
          const cardFirstChild = clickedSlotCard.firstElementChild;
          const timingSection = cardFirstChild?.firstElementChild;
          const isClickingTimingSection = timingSection && (timingSection.contains(target) || timingSection === target);
          const isClickingMiddleSection = target.closest('[data-cursor-element-id="cursor-el-195"]');

          // Don't close if clicking on timing or middle sections - let those handlers manage expansion
          if (isClickingTimingSection || isClickingMiddleSection) {
            return;
          }
        }

        // Check if clicking on the end-timing section (to open end-timing form)
        const isClickingEndTiming =
          !!target.closest('[data-end-timing-form]') ||
          (!!target.closest('[data-testid="flow-slot"]') && !!target.closest('[data-start-end-item]')) ||
          !!target.closest('[data-start-end-item]');

        // Also treat the add-new-slot button like a navigation click: don't swallow the click.
        const clickedButton = target.closest('button') as HTMLButtonElement | null;
        const buttonText = clickedButton?.textContent?.trim() ?? '';
        const isClickingAddNew =
          !!target.closest(
            '[data-add-slot], [data-testid="add-slot"], [aria-label="NEW+"], [aria-label="New"], [aria-label="Add" ]',
          ) ||
          buttonText === 'NEW+' ||
          buttonText.startsWith('NEW') ||
          buttonText.includes('NEW+');

        if (isClickingAddNew) {
          isResettingRef.current = true;

          const originalSlot = flow?.slots?.find((s) => s.id === slot.id) ?? slot;

          reset({
            durationMinutes: minuteOffsetToTime(
              originalSlot?.plannedDurationMinutes ?? originalSlot?.computedDurationMinutes ?? 0,
            ),
            offsetMinutes: minuteOffsetToTime(
              originalSlot?.plannedOffsetMinutes !== undefined
                ? flowStartOffset + originalSlot.plannedOffsetMinutes
                : flowStartOffset + (originalSlot?.computedOffsetMinutes ?? 0),
            ),
          });

          flushSync(() => {
            setLocalSlot(originalSlot);
            setShowFixedTime(originalSlot?.plannedOffsetMinutes !== undefined);
            setCurrentDuration(originalSlot?.plannedDurationMinutes ?? originalSlot?.computedDurationMinutes ?? 0);
            cancelChanges?.();
          });

          const closeDelay = isTouchEvent ? 250 : 0;
          setTimeout(() => {
            onClose();
          }, closeDelay);

          return;
        }

        // If clicking on end-timing section, don't close here - let the onClick handler handle it
        // The onClick handler will close this form and open the end-timing form
        if (isClickingEndTiming) {
          return;
        }

        // If the user clicks another slot card, we must revert preview changes immediately,
        // but we must NOT unmount this form synchronously, otherwise the original click
        // can get swallowed and the user needs a second click.
        if (isClickingAnotherSlot) {
          isResettingRef.current = true;

          // Prefer the original slot from the current flow to avoid carrying over preview changes
          const originalSlot = flow?.slots?.find((s) => s.id === slot.id) ?? slot;

          // Reset local form state right away (prevents brief UI flicker)
          reset({
            durationMinutes: minuteOffsetToTime(
              originalSlot?.plannedDurationMinutes ?? originalSlot?.computedDurationMinutes ?? 0,
            ),
            offsetMinutes: minuteOffsetToTime(
              originalSlot?.plannedOffsetMinutes !== undefined
                ? flowStartOffset + originalSlot.plannedOffsetMinutes
                : flowStartOffset + (originalSlot?.computedOffsetMinutes ?? 0),
            ),
          });

          // Revert preview changes synchronously so the next slot opens against the clean state.
          flushSync(() => {
            setLocalSlot(originalSlot);
            setShowFixedTime(originalSlot?.plannedOffsetMinutes !== undefined);
            setCurrentDuration(originalSlot?.plannedDurationMinutes ?? originalSlot?.computedDurationMinutes ?? 0);
            cancelChanges?.();
          });

          // Delay unmount/close to allow the user's click to perform its intended action.
          const closeDelay = isTouchEvent ? 250 : 0;
          setTimeout(() => {
            onClose();
          }, closeDelay);

          return;
        }

        // For all outside clicks (including clicking on slot cards), close the form
        const delay = isTouchEvent ? 100 : 0;

        // Set resetting flag to prevent preview updates during reset
        isResettingRef.current = true;

        // Reset form values FIRST (synchronously) before cancelChanges (which unmounts the component)
        reset({
          durationMinutes: minuteOffsetToTime(slot?.plannedDurationMinutes ?? slot?.computedDurationMinutes ?? 0),
          offsetMinutes: minuteOffsetToTime(
            slot?.plannedOffsetMinutes !== undefined
              ? flowStartOffset + slot.plannedOffsetMinutes
              : flowStartOffset + (slot?.computedOffsetMinutes ?? 0),
          ),
        });
        setLocalSlot(slot);
        setShowFixedTime(slot?.plannedOffsetMinutes !== undefined);
        setCurrentDuration(slot?.plannedDurationMinutes ?? slot?.computedDurationMinutes ?? 0);

        // Use requestAnimationFrame twice to ensure the event loop processes the click
        // This allows the click event to fire and execute before we close the form
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Additional delay for touch events to ensure tap completes
            setTimeout(() => {
              // Now cancel preview changes and close (this will unmount the component)
              cancelChanges?.();
              onClose();
            }, delay);
          });
        });
      },
      [cancelChanges, onClose, reset, slot, flowStartOffset, flow],
    );

    // Handle Escape key to close form
    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleCancel();
        }
      };
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }, [handleCancel]);

    const hasUnsavedTimingChanges = formState.isDirty || showFixedTime !== initialShowFixedTime;

    const onSubmit = useCallback(
      async (data: SlotTimingFormType) => {
        if (!flow || !localSlot) return;

        const duration = durationMinutes ? dateToMinutesFromMidnight(durationMinutes) : 0;

        if (
          isUntimed &&
          localSlot.contentType === 'Session' &&
          typeof localSlot.content === 'object' &&
          localSlot.content
        ) {
          const sessionId = localSlot.content.id;
          const session = sessions?.find((s) => s.id === sessionId);
          if (!session) {
            notification.notifyError(t('flows.detail.slotTiming.notification.updateError'));
            return;
          }
          const content = localSlot.content as SessionDto;
          const optimisticSlot: FlowSlotDto = {
            ...localSlot,
            plannedDurationMinutes: duration > 0 ? duration : undefined,
            computedDurationMinutes: duration > 0 ? duration : localSlot.computedDurationMinutes,
            content: {
              ...content,
              plannedDurationMinutes: duration > 0 ? duration : undefined,
            },
          };
          onSlotUpdate?.(optimisticSlot);
          try {
            const updatedSession = await updateSessionMutation({
              id: session.id,
              version: session.version,
              plannedDurationMinutes: duration > 0 ? duration : undefined,
            });
            const updatedSlot: FlowSlotDto = {
              ...optimisticSlot,
              content: {
                ...(optimisticSlot.content as SessionDto),
                plannedDurationMinutes: duration > 0 ? duration : undefined,
                version: updatedSession.version,
              },
            };
            onSlotUpdate?.(updatedSlot);
            notification.notifySuccess(t('flows.detail.slotTiming.notification.updateSuccess'));
            onClose();
          } catch {
            onSlotUpdate?.(slot);
            notification.notifyError(t('flows.detail.slotTiming.notification.updateError'));
          }
          return;
        }

        let offset: number | undefined = undefined;
        if (showFixedTime && data.offsetMinutes) {
          offset = dateToMinutesFromMidnight(data.offsetMinutes);
          offset -= flowStartOffset;
        }

        try {
          // Ensure computedOffsetMinutes is always set - use localSlot's value or calculate from slot position
          // If localSlot doesn't have it, it will be computed by handleSlotUpdate via computeAllSlotTimings
          // But we ensure it's explicitly included so it's sent to the database
          const updatedSlot: FlowSlotDto = {
            ...localSlot,
            plannedDurationMinutes: duration > 0 ? duration : undefined,
            plannedOffsetMinutes: offset,
            // Always include computedOffsetMinutes - preserve from localSlot if available, otherwise from slot prop
            // This ensures slots without fixed start-time have their computed time saved
            computedOffsetMinutes: localSlot.computedOffsetMinutes ?? slot.computedOffsetMinutes,
            computedDurationMinutes: localSlot.computedDurationMinutes ?? slot.computedDurationMinutes,
          };
          onSlotUpdate?.(updatedSlot);
          notification.notifySuccess(t('flows.detail.slotTiming.notification.updateSuccess'));
          onClose();
        } catch {
          notification.notifyError(t('flows.detail.slotTiming.notification.updateError'));
        }
      },
      [
        flow,
        isUntimed,
        localSlot,
        durationMinutes,
        sessions,
        updateSessionMutation,
        showFixedTime,
        flowStartOffset,
        slot,
        onSlotUpdate,
        notification,
        t,
        onClose,
      ],
    );

    return (
      <>
        <ClickAwayListener onClickAway={handleClickAway}>
          <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 0, width: '100%', height: '100%' }}>
            <SlotListExpandedLeftColumn layout={columnLayout}>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ width: '100%', minHeight: 0, flex: 1, boxSizing: 'border-box' }}
                data-cursor-element-id="cursor-el-898"
              >
                {/* Decrement button (-) */}
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  disabled={timingMutationDisabled}
                  onMouseDown={() => handleLongPressStart(-1)}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onPointerDown={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressStart(-1);
                    }
                  }}
                  onPointerUp={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onPointerCancel={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onPointerLeave={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    handleLongPressStart(-1);
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    const wasLongPress = isLongPressingRef.current || !!longPressIntervalRef.current;
                    const touchDuration = Date.now() - touchStartTimeRef.current;
                    handleLongPressEnd();
                    if (!wasLongPress && touchDuration < 500) {
                      setTimeout(() => {
                        if (!longPressIntervalRef.current && !isLongPressingRef.current) {
                          const newTime = format(addMinutes(parse(durationAsTime, 'HH:mm', new Date()), -1), 'HH:mm');
                          handleDurationChange(newTime);
                        }
                      }, 10);
                    }
                  }}
                  onTouchCancel={(e) => {
                    e.stopPropagation();
                    handleLongPressEnd();
                  }}
                  onClick={(e) => {
                    if (isLongPressingRef.current || longPressIntervalRef.current) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    const newTime = format(addMinutes(parse(durationAsTime, 'HH:mm', new Date()), -1), 'HH:mm');
                    handleDurationChange(newTime);
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    touchAction: 'manipulation',
                    ...slotInlineOutlinedActionSx,
                  }}
                >
                  <ArrowDropDownOutlined fontSize="large" />
                </Button>
                {/* Increment button (+) */}
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  disabled={timingMutationDisabled}
                  onMouseDown={() => handleLongPressStart(1)}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onPointerDown={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressStart(1);
                    }
                  }}
                  onPointerUp={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onPointerCancel={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onPointerLeave={(e) => {
                    if (isTouchDevice) {
                      e.stopPropagation();
                      handleLongPressEnd();
                    }
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    handleLongPressStart(1);
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    const wasLongPress = isLongPressingRef.current || !!longPressIntervalRef.current;
                    const touchDuration = Date.now() - touchStartTimeRef.current;
                    handleLongPressEnd();
                    if (!wasLongPress && touchDuration < 500) {
                      setTimeout(() => {
                        if (!longPressIntervalRef.current && !isLongPressingRef.current) {
                          const newTime = format(addMinutes(parse(durationAsTime, 'HH:mm', new Date()), 1), 'HH:mm');
                          handleDurationChange(newTime);
                        }
                      }, 10);
                    }
                  }}
                  onTouchCancel={(e) => {
                    e.stopPropagation();
                    handleLongPressEnd();
                  }}
                  onClick={(e) => {
                    if (isLongPressingRef.current || longPressIntervalRef.current) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    const newTime = format(addMinutes(parse(durationAsTime, 'HH:mm', new Date()), 1), 'HH:mm');
                    handleDurationChange(newTime);
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    touchAction: 'manipulation',
                    ...slotInlineOutlinedActionSx,
                  }}
                >
                  <ArrowDropUpOutlined fontSize="large" />
                </Button>
              </Stack>
            </SlotListExpandedLeftColumn>
            <Divider orientation="vertical" flexItem />
            {/* Middle: equal-width buttons in one row; full width of the timing strip */}
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                ...slotListExpandedColumnPaddingSx,
                flex: 1,
                minWidth: 0,
                width: '100%',
                // Default Button minWidth (64px) × 4 exceeds narrow strips; keep gap visible
                gap: 1.5,
              }}
            >
              {/* Hidden time picker when fixed time — needed for ref / left-column edit */}
              {!isUntimed && showFixedTime && (
                <Controller
                  name="offsetMinutes"
                  control={control}
                  render={({ field }) => (
                    <Box sx={{ display: 'none' }}>
                      <TimePicker
                        ampm={false}
                        views={['hours', 'minutes']}
                        value={
                          pendingTimeValue
                            ? parse(pendingTimeValue, 'HH:mm', new Date())
                            : field.value
                              ? parse(field.value, 'HH:mm', new Date())
                              : null
                        }
                        minTime={parse(minuteOffsetToTime(flowStartOffset), 'HH:mm', new Date())}
                        maxTime={parse(minuteOffsetToTime(flowEndOffset), 'HH:mm', new Date())}
                        timeSteps={{ minutes: 1 }}
                        forceDesktopMode={true}
                        closeOnSelect={false}
                        open={isTimePickerOpen}
                        onOpen={() => {
                          setIsTimePickerOpen(true);
                          isSelectingTimeRef.current = false;
                          setPendingTimeValue(field.value || null);
                        }}
                        onClose={() => {
                          isSelectingTimeRef.current = false;
                          setIsTimePickerOpen(false);
                          setPendingTimeValue(null);
                          setTimeout(() => {
                            isSelectingTimeRef.current = false;
                          }, 100);
                        }}
                        onChange={(value) => {
                          isSelectingTimeRef.current = true;
                          if (value) {
                            setPendingTimeValue(format(value, 'HH:mm'));
                          } else {
                            setPendingTimeValue(null);
                          }
                        }}
                        onAccept={(value) => {
                          if (value) {
                            const newTime = format(value, 'HH:mm');
                            field.onChange(newTime);
                            setPendingTimeValue(null);
                            isSelectingTimeRef.current = false;
                            setIsTimePickerOpen(false);
                          }
                        }}
                        slotProps={{
                          popper: {
                            ...(fixedStartPickerAnchorEl ? { anchorEl: fixedStartPickerAnchorEl } : {}),
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, 8],
                                },
                              },
                              {
                                name: 'preventOverflow',
                                enabled: true,
                                options: {
                                  boundary: 'viewport',
                                  padding: 8,
                                },
                              },
                              {
                                name: 'flip',
                                enabled: true,
                              },
                            ],
                            placement: 'bottom-start',
                            onClick: (e: React.MouseEvent) => {
                              e.stopPropagation();
                            },
                          },
                          actionBar: {
                            actions: ['cancel', 'accept'],
                          },
                          textField: {
                            sx: {
                              display: 'none',
                            },
                          },
                        }}
                      />
                    </Box>
                  )}
                />
              )}
              {!isUntimed && (
                <Box
                  ref={setFixedStartPickerAnchorEl}
                  sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}
                >
                  <Tooltip title={t('flows.detail.slotTiming.fixedTime')} leaveDelay={0} disableInteractive>
                    <Box component="span" sx={{ display: 'block', width: '100%' }}>
                      <Button
                        fullWidth
                        variant={showFixedTime ? 'contained' : 'outlined'}
                        color="inherit"
                        size="small"
                        disableElevation
                        disableRipple
                        onClick={handleToggleFixedTime}
                        disabled={timingMutationDisabled}
                        sx={{
                          minWidth: 0,
                          ...(showFixedTime ? slotInlineContainedActionSx(theme) : slotInlineOutlinedActionSx),
                        }}
                      >
                        <LockClockOutlined fontSize="small" />
                      </Button>
                    </Box>
                  </Tooltip>
                </Box>
              )}

              {isUntimed || hasUnsavedTimingChanges ? (
                <>
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="inherit"
                      size="small"
                      onClick={handleCancel}
                      sx={{
                        minWidth: 0,
                        ...slotInlineOutlinedActionSx,
                      }}
                    >
                      <CancelOutlinedIcon fontSize="small" />
                    </Button>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={handleSubmit(onSubmit)}
                      disabled={!hasUnsavedTimingChanges || scheduleLiveLockBlocked}
                      sx={{ minWidth: 0 }}
                    >
                      <CheckCircleIcon fontSize="small" />
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  {onShowForm && (
                    <>
                      {showSplitSessionButton ? (
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                          <Tooltip
                            title={t('flows.detail.slotList.detail.splitSession.tooltip')}
                            leaveDelay={0}
                            disableInteractive
                          >
                            <Box component="span" sx={{ display: 'block', width: '100%' }}>
                              <Button
                                fullWidth
                                variant="outlined"
                                color="inherit"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleSplitSession();
                                }}
                                disabled={timingMutationDisabled || splitPending || deletePending}
                                data-testid="split-session-segment-button"
                                sx={{ minWidth: 0, ...slotInlineOutlinedActionSx }}
                              >
                                <PiSplitVertical size={18} />
                              </Button>
                            </Box>
                          </Tooltip>
                        </Box>
                      ) : null}
                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                        <Tooltip
                          title={t('flows.detail.slotList.actions.addSlotAbove')}
                          leaveDelay={0}
                          disableInteractive
                        >
                          <Box component="span" sx={{ display: 'block', width: '100%' }}>
                            <Button
                              fullWidth
                              variant="outlined"
                              color="inherit"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (slot?.id && onShowForm) {
                                  onShowForm('', previousSlotEndOffset, undefined, slot.id, undefined);
                                  onClose();
                                }
                              }}
                              disabled={timingMutationDisabled}
                              data-testid="add-slot-above-button"
                              sx={{ minWidth: 0, ...slotInlineOutlinedActionSx }}
                            >
                              <LuListStart size={18} />
                            </Button>
                          </Box>
                        </Tooltip>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                        <Tooltip
                          title={t('flows.detail.slotList.actions.addSlotBelow')}
                          leaveDelay={0}
                          disableInteractive
                        >
                          <Box component="span" sx={{ display: 'block', width: '100%' }}>
                            <Button
                              fullWidth
                              variant="outlined"
                              color="inherit"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (slot?.id && onShowForm) {
                                  onShowForm('', slotEndOffset, undefined, undefined, slot.id);
                                  onClose();
                                }
                              }}
                              disabled={timingMutationDisabled}
                              data-testid="add-slot-after-button"
                              sx={{ minWidth: 0, ...slotInlineOutlinedActionSx }}
                            >
                              <LuListEnd size={18} />
                            </Button>
                          </Box>
                        </Tooltip>
                      </Box>
                    </>
                  )}

                  {hasFixedBoundary && gapBeforeFixedSlot > 0 ? (
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                      <Tooltip title={t('flows.detail.slotTiming.tooltips.expand')} leaveDelay={0} disableInteractive>
                        <Box component="span" sx={{ display: 'block', width: '100%' }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="inherit"
                            size="small"
                            onClick={handleExpandToFillGap}
                            disabled={timingMutationDisabled}
                            sx={{ minWidth: 0, ...slotInlineOutlinedActionSx }}
                          >
                            <Expand fontSize="small" />
                          </Button>
                        </Box>
                      </Tooltip>
                    </Box>
                  ) : hasFixedBoundary && (gapBeforeFixedSlot < 0 || slotShortenedBy > 0) ? (
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                      <Tooltip title={t('flows.detail.slotTiming.tooltips.compress')} leaveDelay={0} disableInteractive>
                        <Box component="span" sx={{ display: 'block', width: '100%' }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="warning"
                            size="small"
                            onClick={handleShorten}
                            disabled={timingMutationDisabled}
                            sx={{ minWidth: 0 }}
                          >
                            <Compress fontSize="small" />
                          </Button>
                        </Box>
                      </Tooltip>
                    </Box>
                  ) : null}
                </>
              )}
            </Stack>
          </Stack>
        </ClickAwayListener>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          open={isDeleteDialogOpen}
          isLoading={deletePending}
          title={
            isDeletingSessionNotInLibrary
              ? t('flows.detail.slotList.detail.deleteSlot.sessionTitle')
              : t('flows.detail.slotList.detail.deleteSlot.removeSessionTitle')
          }
          text={
            isDeletingSessionNotInLibrary
              ? t('flows.detail.slotList.detail.deleteSlot.sessionText', { title: sessionTitle || '' })
              : t('flows.detail.slotList.detail.deleteSlot.removeSessionText', { title: sessionTitle || '' })
          }
          onConfirm={handleDeleteSession}
          onCancel={handleDeleteModalCancel}
        />
      </>
    );
  },
);

export default InlineSlotTimingForm;
